"use client";

import { useEffect, useRef, useState } from "react";
import Link from 'next/link';
import { cityService } from '@/lib/services/cityService';

interface City {
  id: string;
  name: string;
  province: string;
  slug: string;
  description: string;
  displaySettings: {
    emoji: string;
    colorGradient: string;
    borderClass: string;
  };
  displayOrder: number;
}

export default function CitiesSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCities = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await cityService.getActive();
        
        // Add "More Coming Soon" card at the end
        const citiesWithComingSoon = [
          ...data,
          {
            id: 'coming-soon',
            name: 'More Coming Soon',
            province: 'Ontario',
            slug: '',
            description: "We\'re expanding! New cities are being added regularly.",
            displaySettings: {
              emoji: '📍',
              colorGradient: 'from-border-gray/50 to-light-blue',
              borderClass: 'border-border-gray',
            },
            displayOrder: 999,
          },
        ];
        
        setCities(citiesWithComingSoon);
      } catch (err: any) {
        console.error('Failed to fetch cities:', err);
        setError(err.message || 'Failed to load cities');
      } finally {
        setLoading(false);
      }
    };

    fetchCities();
  }, []);

  useEffect(() => {
    const items = ref?.current?.querySelectorAll(".city-card");
    if (!items) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible", "animate-fade-up");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    items?.forEach((item) => observer?.observe(item));
    return () => observer?.disconnect();
  }, [cities]);

  return (
    <section id="cities" className="py-28 bg-light-blue relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, #4732D7 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <div className="section-label mx-auto w-fit mb-6">
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            Service Areas
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-deep-navy leading-tight mb-4">
            GoGedit in Your City
          </h2>
          <p className="text-body-text text-lg max-w-2xl mx-auto">
            Available across Ontario. Find drivers near you and get your large items moved today.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="w-12 h-12 border-4 border-brand-purple border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-brand-purple text-white rounded-lg hover:bg-opacity-90 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : cities.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-body-text text-lg">No cities available at the moment.</p>
          </div>
        ) : (
          <div ref={ref} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {cities?.map((city, idx) => (
              <div
                key={city?.id}
                className={`city-card on-scroll stagger-${Math.min(idx + 1, 5)} bento-card bg-gradient-to-br ${city?.displaySettings?.colorGradient} border ${city?.displaySettings?.borderClass} p-6`}
              >
                <div className="flex items-start justify-between mb-4">
                  <span className="text-3xl">{city?.displaySettings?.emoji}</span>
                  {city?.slug && (
                    <span className="bg-white text-brand-purple text-xs font-bold px-3 py-1 rounded-full border border-brand-purple/20">
                      Active
                    </span>
                  )}
                </div>
                <h3 className="font-heading text-xl font-bold text-deep-navy mb-1">
                  {city?.name}
                  {city?.slug && (
                    <span className="text-body-text font-normal text-sm ml-2">{city?.province}</span>
                  )}
                </h3>
                <p className="text-body-text text-sm leading-relaxed mb-4">{city?.description}</p>
                {city?.slug ? (
                  <Link
                    href={`/cities/${city?.slug}`}
                    className="inline-flex items-center gap-1 text-brand-purple font-bold text-sm hover:gap-2 transition-all"
                  >
                    Learn more
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </Link>
                ) : (
                  <span className="text-muted-text text-sm font-medium">Expanding soon...</span>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="text-center mt-12">
          <p className="text-lg text-body-text mb-4">
            Want to become a driver and serve your city?
          </p>
          <Link
            href="/be-a-driver"
            className="inline-block bg-brand-purple text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-opacity-90 transition-all"
          >
            Join as a Driver
          </Link>
        </div>
      </div>
    </section>
  );
}